import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import { ComparisonOperator } from 'cio/utils/operators';
import CampaignModel from 'cio/models/campaign';
import GroupCondition from 'cio/app-models/conditions/group';
import NewsletterModel from 'cio/models/newsletter';
import { SerializedLegacyAttributeFilter } from 'cio/types/conditions/legacy-filter';
import { Filters } from 'cio/utils/filters';
import Model from '@ember-data/model';
import { deserializeFilter } from './conditions/deserialize/attribute';
import deserializeFilters from './conditions/deserialize/filter';

export enum ModelType {
  Campaign = 'campaign',
  Newsletter = 'newsletter',
}

export interface ModelWithConversion extends Model {
  conversion: boolean | null;
  conversionType: string;
  conversionWindow: number;
  conversionAction: string;
  validConversionTimeframe: boolean;
  isSegmentConversion: boolean;
  isEventConversion: boolean;

  conversionSegmentId: string | null;

  conversionEventName: string | null;
  conversionEventFilters: Filters | null;
}

export enum ConversionType {
  EnterSegment = 'enter',
  ExitSegment = 'exit',
  PerformEvent = 'perform_event',
}

export enum ConversionTimeframe {
  Within = 'within',
  AnyTime = 'any_time',
}

export const CONVERSION_TYPES = Object.freeze([
  { groupName: 'Event', options: [{ value: ConversionType.PerformEvent, label: 'performs event' }] },

  {
    groupName: 'Segment',
    options: [
      { value: ConversionType.EnterSegment, label: 'enters segment' },
      { value: ConversionType.ExitSegment, label: 'leaves segment' },
    ],
  },
]);

export function modelType(model: ModelWithConversion) {
  if (model instanceof CampaignModel) {
    return ModelType.Campaign;
  } else if (model instanceof NewsletterModel) {
    return ModelType.Newsletter;
  }

  throw new TypeError(
    // @ts-ignore - private/internal properties aren't typed
    `You're trying to use a ${model._internalModal.modelName} model with the Conversions::Summary component, which isn't supported.`,
  );
}

export function isConversionSegmentBased(model: ModelWithConversion) {
  if (model.conversionType === '') {
    return false;
  }

  return [ConversionType.EnterSegment, ConversionType.ExitSegment].includes(model.conversionType as any);
}

export function isConversionEventBased(model: ModelWithConversion) {
  return model.conversionType === ConversionType.PerformEvent;
}

export function createConversionGroupCondition(model: ModelWithConversion) {
  if (model.conversionEventFilters == null) {
    return null;
  }

  // TODO: We need serializers to go straight from AbstractConditions to Filters (v2)
  // This is a circuitous route to go to legacy Conditions and then to Filters
  let conditions = deserializeFilters(model.conversionEventFilters).map((filter) => {
    return deserializeFilter({
      ...filter,
      type: 'event_attribute',
      event: model.conversionEventName,
      isConversionEventAttribute: true,
      isForeignEventFilterAttribute: true,
    } as any as SerializedLegacyAttributeFilter);
  });

  return GroupCondition.create({
    children: conditions as any,
    operator: Object.keys(model.conversionEventFilters)[0] as 'and' | 'or',
  });
}

export function hasEventAttributeComparison(eventCondition: GroupCondition | null): boolean {
  if (eventCondition == null) {
    return false;
  }
  return eventCondition?.children.some(
    (condition: (AttributeCondition | EventAttributeCondition) & { comparisonOperator: ComparisonOperator }) => {
      return isUsingEventAttributeComparison(condition.comparisonOperator);
    },
  );
}

export function isUsingEventAttributeComparison(comparisonOperator: ComparisonOperator) {
  return comparisonOperator === ComparisonOperator.Event;
}

// whilst setting a goal, we allow users to set filters and also switch to no-goal setting mode at any point without losing
// progress in setting up filters. We do not want to consider these filters if they choose not to have a goal.
export function showConversionEventErrors(model: CampaignModel | NewsletterModel, hasConditionErrors: boolean) {
  let showEventConditionErrors = false;
  if (model.conversion === true) {
    showEventConditionErrors = hasConditionErrors;
  }
  return !model.validConversion || showEventConditionErrors;
}
